import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { UserPlus, Loader2, Copy, Check, ChevronDown, ChevronUp, Pencil, Trash2, X, Save, RefreshCw, Power, ToggleLeft, ToggleRight, Calendar, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UsersTab({ token }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userAlerts, setUserAlerts] = useState({});
  const [loadingAlerts, setLoadingAlerts] = useState({});
  const [editingAlert, setEditingAlert] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [togglingSubscription, setTogglingSubscription] = useState(null);
  const [togglingAlert, setTogglingAlert] = useState(null);
  const [extendingTrial, setExtendingTrial] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [deletingUser, setDeletingUser] = useState(null);
  
  // Email modal state
  const [emailModal, setEmailModal] = useState({ open: false, user: null });
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    trial_days: 30
  });

  const fetchUsers = () => {
    fetch(`${API_URL}/api/admin/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setUsers(data.users || []);
        setTotal(data.total || 0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const toggleUser = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      if (!userAlerts[userId]) {
        setLoadingAlerts(prev => ({ ...prev, [userId]: true }));
        fetch(`${API_URL}/api/admin/users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(res => res.json())
          .then(data => setUserAlerts(prev => ({ ...prev, [userId]: data.alerts || [] })))
          .finally(() => setLoadingAlerts(prev => ({ ...prev, [userId]: false })));
      }
    }
  };

  const startEditAlert = (alert) => {
    setEditingAlert(alert.id);
    setEditForm({
      grade: alert.grade || '',
      location: alert.location || alert.region || '',
      occupation: alert.occupation || '',
      keywords: alert.keywords || ''
    });
  };

  const cancelEdit = () => {
    setEditingAlert(null);
    setEditForm({});
  };

  const saveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/alerts/${alertId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Alert updated');
      setEditingAlert(null);
      // Refresh alerts for this user
      setUserAlerts(prev => ({ ...prev, [expandedUser]: null }));
      toggleUser(expandedUser);
      setTimeout(() => toggleUser(expandedUser), 100);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteAlert = async (alertId, alertName) => {
    if (!window.confirm(`Delete "${alertName}"? This will also delete all job matches.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Alert deleted');
      setUserAlerts(prev => ({
        ...prev,
        [expandedUser]: prev[expandedUser]?.filter(a => a.id !== alertId)
      }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || 'Server returned invalid response');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create user');
      }
      
      setNewUser({
        ...data.user,
        temp_password: data.temp_password,
        email_sent: data.email_sent
      });
      
      toast.success('User created successfully!');
      fetchUsers();
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(newUser.temp_password);
    setCopiedPassword(true);
    toast.success('Password copied to clipboard!');
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const resetForm = () => {
    setShowAddForm(false);
    setNewUser(null);
    setFormData({ email: '', full_name: '', trial_days: 30 });
  };

  const handleSyncStripe = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/sync-stripe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.deactivated_count > 0) {
        toast.success(`Synced! Deactivated ${data.deactivated_count} user(s): ${data.deactivated_users.join(', ')}`);
      } else {
        toast.success('All users are in sync with Stripe');
      }
      if (data.skipped_count > 0) {
        toast.info(`Skipped ${data.skipped_count} user(s) (no Stripe IDs or API errors)`);
      }
      fetchUsers();
    } catch (err) {
      toast.error('Stripe sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleSubscription = async (e, userId, currentActive, userEmail) => {
    e.stopPropagation();
    const action = currentActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} subscription for ${userEmail}?`)) return;
    
    setTogglingSubscription(userId);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/subscription?active=${!currentActive}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success(`Subscription ${!currentActive ? 'activated' : 'deactivated'} for ${userEmail}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingSubscription(null);
    }
  };

  const handleToggleAlert = async (alertId, currentActive) => {
    setTogglingAlert(alertId);
    try {
      const res = await fetch(`${API_URL}/api/admin/alerts/${alertId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to toggle');
      const data = await res.json();
      toast.success(`Alert ${data.active ? 'activated' : 'paused'}`);
      // Refresh alerts for this user
      setUserAlerts(prev => ({
        ...prev,
        [expandedUser]: prev[expandedUser]?.map(a => 
          a.id === alertId ? { ...a, active: data.active } : a
        )
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingAlert(null);
    }
  };

  const handleExtendTrial = async (userId, userEmail) => {
    setExtendingTrial(userId);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/extend-trial?days=${extendDays}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to extend');
      const data = await res.json();
      toast.success(`Extended ${userEmail} by ${data.days_added} days. New expiry: ${new Date(data.new_expires).toLocaleDateString()}`);
      setExtendingTrial(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExtendingTrial(null);
    }
  };

  const handleDeleteUser = async (e, userId, userEmail) => {
    e.stopPropagation();
    if (!window.confirm(`⚠️ PERMANENTLY DELETE ${userEmail}?\n\nThis will:\n• Cancel their Stripe subscription\n• Delete all their alerts\n• Delete all their data\n\nThis action cannot be undone!`)) return;
    
    // Double confirmation for safety
    if (!window.confirm(`Are you absolutely sure? Type "yes" in your mind and click OK to permanently delete ${userEmail}.`)) return;
    
    setDeletingUser(userId);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      const data = await res.json();
      toast.success(`${userEmail} permanently deleted`);
      setExpandedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingUser(null);
    }
  };

  // Email functions
  const openEmailModal = (user) => {
    setEmailModal({ open: true, user });
    setEmailForm({ 
      subject: '', 
      body: `Hi ${user.full_name || 'there'},\n\n\n\nBest wishes,\nTrac Jobs Alert Team` 
    });
  };

  const closeEmailModal = () => {
    setEmailModal({ open: false, user: null });
    setEmailForm({ subject: '', body: '' });
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!emailForm.body.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingEmail(true);
    try {
      // Convert plain text body to HTML (preserve line breaks)
      const htmlContent = emailForm.body
        .split('\n')
        .map(line => line.trim() === '' ? '<br>' : `<p>${line}</p>`)
        .join('');

      const res = await fetch(`${API_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_email: emailModal.user.email,
          subject: emailForm.subject,
          html_content: htmlContent
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send email');
      }
      
      toast.success(`Email sent to ${emailModal.user.email}`);
      closeEmailModal();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Add User Card */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Add New User</CardTitle>
          {!showAddForm && !newUser && (
            <Button 
              onClick={() => setShowAddForm(true)}
              className="bg-[#00A651] hover:bg-[#008C44]"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          )}
        </CardHeader>
        
        {(showAddForm || newUser) && (
          <CardContent>
            {newUser ? (
              <div className="bg-green-900/20 border border-green-600 rounded-lg p-6">
                <h3 className="text-green-400 font-semibold text-lg mb-4">✅ User Created Successfully!</h3>
                
                <div className="space-y-3 text-white">
                  <p><span className="text-slate-400">Name:</span> {newUser.full_name}</p>
                  <p><span className="text-slate-400">Email:</span> {newUser.email}</p>
                  <p><span className="text-slate-400">Trial Period:</span> {newUser.trial_days} days</p>
                  <p><span className="text-slate-400">Expires:</span> {new Date(newUser.expires).toLocaleDateString()}</p>
                  
                  <div className="bg-slate-900 rounded-lg p-4 mt-4">
                    <p className="text-slate-400 text-sm mb-2">Temporary Password:</p>
                    <div className="flex items-center gap-3">
                      <code className="text-xl font-mono text-[#00A651] bg-slate-800 px-4 py-2 rounded">
                        {newUser.temp_password}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyPassword}
                        className="text-white border-slate-600"
                      >
                        {copiedPassword ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-sm mt-4">
                    {newUser.email_sent ? (
                      <span className="text-green-400">✅ Welcome email sent to user</span>
                    ) : (
                      <span className="text-yellow-400">⚠️ Welcome email could not be sent. Please share credentials manually.</span>
                    )}
                  </p>
                </div>
                
                <Button 
                  onClick={resetForm}
                  className="mt-6 bg-[#00A651] hover:bg-[#008C44]"
                >
                  Add Another User
                </Button>
              </div>
            ) : (
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Full Name</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      placeholder="Dr. John Smith"
                      required
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Enter user email"
                      required
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Free Trial Period</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[7, 14, 30, 60, 90].map((days) => (
                      <Button
                        key={days}
                        type="button"
                        variant={formData.trial_days === days ? "default" : "outline"}
                        className={formData.trial_days === days 
                          ? "bg-[#00A651] hover:bg-[#008C44]" 
                          : "text-white border-slate-600 hover:bg-slate-700"}
                        onClick={() => setFormData({...formData, trial_days: days})}
                      >
                        {days} days
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-slate-400 text-sm">Or custom:</span>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.trial_days}
                      onChange={(e) => setFormData({...formData, trial_days: parseInt(e.target.value) || 30})}
                      className="w-24 bg-slate-900 border-slate-600 text-white"
                    />
                    <span className="text-slate-400 text-sm">days</span>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={creating}
                    className="bg-[#00A651] hover:bg-[#008C44]"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create User
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="text-white border-slate-600"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        )}
      </Card>

      {/* Users List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">All Users ({total})</CardTitle>
          <Button
            onClick={handleSyncStripe}
            disabled={syncing}
            variant="outline"
            className="text-white border-slate-600 hover:bg-indigo-600 hover:border-indigo-600"
            data-testid="sync-stripe-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync with Stripe'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.map((user) => (
              <div key={user.id} className="border border-slate-700 rounded-lg overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 bg-slate-800/50 cursor-pointer hover:bg-slate-700/50"
                  onClick={() => toggleUser(user.id)}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate">{user.email}</p>
                      <p className="text-slate-400 text-sm">{user.full_name || '-'}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className={user.subscription_active ? 'bg-green-500' : 'bg-slate-500'}>
                        {user.subscription_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {user.trial_account && <Badge className="bg-blue-500">Trial</Badge>}
                      <Badge className="bg-slate-600">{user.alerts_count || 0} alerts</Badge>
                      {!user.is_admin && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openEmailModal(user); }}
                            className="h-7 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                            data-testid={`email-user-${user.id}`}
                            title="Send email to user"
                          >
                            <Mail className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={togglingSubscription === user.id}
                            onClick={(e) => handleToggleSubscription(e, user.id, user.subscription_active, user.email)}
                            className={`h-7 px-2 ${user.subscription_active ? 'text-red-400 hover:text-red-300 hover:bg-red-900/30' : 'text-green-400 hover:text-green-300 hover:bg-green-900/30'}`}
                            data-testid={`toggle-sub-${user.id}`}
                            title={user.subscription_active ? 'Deactivate subscription' : 'Activate subscription'}
                          >
                            {togglingSubscription === user.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Power className="w-3 h-3" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={deletingUser === user.id}
                            onClick={(e) => handleDeleteUser(e, user.id, user.email)}
                            className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                            data-testid={`delete-user-${user.id}`}
                            title="Permanently delete user"
                          >
                            {deletingUser === user.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    {expandedUser === user.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
                {expandedUser === user.id && (
                  <div className="p-4 bg-slate-900 border-t border-slate-700">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div><p className="text-slate-400">Created</p><p className="text-white">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</p></div>
                      <div><p className="text-slate-400">Expires</p><p className="text-white">{user.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'Never'}</p></div>
                      <div className="col-span-2">
                        {!user.is_admin && (
                          <div className="flex items-center gap-2">
                            <p className="text-slate-400 text-sm">Extend:</p>
                            <select 
                              value={extendDays} 
                              onChange={(e) => setExtendDays(Number(e.target.value))}
                              className="bg-slate-700 border border-slate-600 text-white text-sm rounded px-2 py-1"
                            >
                              <option value={7}>7 days</option>
                              <option value={14}>14 days</option>
                              <option value={30}>30 days</option>
                              <option value={60}>60 days</option>
                              <option value={90}>90 days</option>
                            </select>
                            <Button
                              size="sm"
                              onClick={() => handleExtendTrial(user.id, user.email)}
                              disabled={extendingTrial === user.id}
                              className="bg-blue-600 hover:bg-blue-500 h-7 px-3 text-xs"
                              data-testid={`extend-trial-${user.id}`}
                            >
                              {extendingTrial === user.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3 mr-1" />}
                              Extend
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Job Alerts:</p>
                      {loadingAlerts[user.id] ? (
                        <div className="flex items-center py-2"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
                      ) : userAlerts[user.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {userAlerts[user.id].map((alert) => (
                            <div key={alert.id} className="p-3 bg-slate-800 rounded-lg">
                              {editingAlert === alert.id ? (
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <Label className="text-slate-400 text-xs">Grade</Label>
                                      <Input
                                        value={editForm.grade}
                                        onChange={(e) => setEditForm({...editForm, grade: e.target.value})}
                                        placeholder="e.g. MT02"
                                        className="bg-slate-900 border-slate-600 text-white h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-slate-400 text-xs">Location</Label>
                                      <Input
                                        value={editForm.location}
                                        onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                                        placeholder="e.g. UK"
                                        className="bg-slate-900 border-slate-600 text-white h-8 text-sm"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => saveAlert(alert.id)} className="bg-green-600 hover:bg-green-700 h-7">
                                      <Save className="w-3 h-3 mr-1" /> Save
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-slate-400 h-7">
                                      <X className="w-3 h-3 mr-1" /> Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <p className="text-white font-medium">{alert.name}</p>
                                      <Badge className={alert.active ? 'bg-green-600 text-xs' : 'bg-yellow-600 text-xs'}>
                                        {alert.active ? 'Active' : 'Paused'}
                                      </Badge>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => handleToggleAlert(alert.id, alert.active)}
                                        disabled={togglingAlert === alert.id}
                                        className={`h-7 px-2 ${alert.active ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'}`}
                                        data-testid={`toggle-alert-${alert.id}`}
                                        title={alert.active ? 'Pause alert' : 'Activate alert'}
                                      >
                                        {togglingAlert === alert.id ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : alert.active ? (
                                          <ToggleRight className="w-4 h-4" />
                                        ) : (
                                          <ToggleLeft className="w-4 h-4" />
                                        )}
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => startEditAlert(alert)} className="text-slate-400 hover:text-blue-400 h-7 px-2">
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => deleteAlert(alert.id, alert.name)} className="text-slate-400 hover:text-red-400 h-7 px-2">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 text-xs">
                                    {alert.grade && <span className="bg-blue-900/50 text-blue-300 px-2 py-1 rounded">Grade: {alert.grade}</span>}
                                    {alert.occupation && <span className="bg-purple-900/50 text-purple-300 px-2 py-1 rounded">Specialty: {alert.occupation}</span>}
                                    {(alert.region || alert.location) && <span className="bg-green-900/50 text-green-300 px-2 py-1 rounded">Location: {alert.region || alert.location}</span>}
                                    {alert.keywords && <span className="bg-orange-900/50 text-orange-300 px-2 py-1 rounded">Keywords: {alert.keywords}</span>}
                                    {!alert.grade && !alert.occupation && !alert.region && !alert.location && <span className="bg-red-900/50 text-red-300 px-2 py-1 rounded">No filters set!</span>}
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">No alerts set up</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Email Modal */}
      {emailModal.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="text-white font-semibold text-lg">Send Email</h3>
                <p className="text-slate-400 text-sm">To: {emailModal.user?.email}</p>
              </div>
              <Button variant="ghost" onClick={closeEmailModal} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              <div>
                <Label className="text-slate-300 mb-2 block">Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject..."
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <Label className="text-slate-300 mb-2 block">Message</Label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Type your message..."
                  rows={12}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-md p-3 resize-none focus:outline-none focus:ring-2 focus:ring-[#00A651]"
                />
                <p className="text-slate-500 text-xs mt-1">Plain text - line breaks will be preserved</p>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700">
              <Button variant="ghost" onClick={closeEmailModal} className="text-slate-400">
                Cancel
              </Button>
              <Button 
                onClick={handleSendEmail} 
                disabled={sendingEmail}
                className="bg-[#00A651] hover:bg-[#008C44]"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
